import { AuthService, ErrorService, VoucherService } from '@/services';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppSidenavComponent, SidenavNavGroup, SidenavNavItem } from '@/components';

@Component({
    selector: 'app-management',
    templateUrl: './management.component.html',
    styleUrls: ['./management.component.scss'],
    standalone: false
})
export class ManagementComponent implements OnInit, OnDestroy {
  @ViewChild(AppSidenavComponent) sidenav: AppSidenavComponent;

  private vouchersSubscription: Subscription;
  private vouchersVisible: boolean = false;

  managementNavGroups: SidenavNavGroup[] = [];

  /** Passed to app-nav-list so clicking a nav item closes the drawer on mobile. */
  readonly closeDrawerFn = () => this.sidenav?.closeDrawerIfMobile();

  constructor(
    private authService: AuthService,
    private voucherService: VoucherService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.managementNavGroups = this.buildNavGroups();

    this.vouchersSubscription = this.voucherService.checkIfInstituteUsesVouchers().subscribe({
      next: required => {
        this.vouchersVisible = required;
        this.managementNavGroups = this.buildNavGroups();
      },
      error: e => this.errorService.onError(e),
    });
  }

  ngOnDestroy(): void {
    this.vouchersSubscription?.unsubscribe();
  }

  isUserTeacher(): boolean {
    return this.authService.isUserTeacher();
  }

  isUserAdmin(): boolean {
    return this.authService.isUserAdmin();
  }

  isUserSuperAdmin(): boolean {
    return this.authService.isUserSuperAdmin();
  }

  private buildNavGroups(): SidenavNavGroup[] {
    const groups: SidenavNavGroup[] = [
      {
        label: 'management.menu.general',
        disableCollapsible: true,
        items: [
          {
            label: 'management.menu.groups',
            path: '/beheer/groepen',
            routerLink: ['/beheer/groepen'],
            order: 0,
          },
        ],
      },
    ];

    if (this.isUserAdmin()) {
      groups[0].items.push({
            label: 'management.menu.users',
            path: '/beheer/gebruikers',
            routerLink: ['/beheer/gebruikers'],
            order: 1,
          },
        );
      groups.push({
        label: 'management.menu.EHR-management',
        disableCollapsible: true,
        items: [
          {
            label: 'management.menu.organizations',
            path: '/beheer/epd/organisaties',
            routerLink: ['/beheer/epd/organisaties'],
            order: 0,
          },
          {
            label: 'management.menu.anamnesis-forms',
            path: '/beheer/epd/formulieren',
            routerLink: ['/beheer/epd/formulieren'],
            order: 1,
          },
          {
            label: 'management.menu.strategies',
            path: '/beheer/epd/strategieen',
            routerLink: ['/beheer/epd/strategieen'],
            order: 2,
          },
          {
            label: 'management.menu.functions',
            path: '/beheer/epd/functies',
            routerLink: ['/beheer/epd/functies'],
            order: 3,
          },
          {
            label: 'management.menu.clients',
            path: '/beheer/epd/clienten',
            routerLink: ['/beheer/epd/clienten'],
            order: 4,
          },
          {
            label: 'management.menu.static-sites',
            path: '/beheer/epd/statische-sites',
            routerLink: ['/beheer/epd/statische-sites'],
            order: 5,
          },
        ],
      });
    }

    if (this.isUserSuperAdmin()) {
      const adminItems: SidenavNavItem[] = [
        {
          label: 'management.menu.admin.dashboard',
          path: '/beheer/admin/dashboard',
          routerLink: ['/beheer/admin/dashboard'],
          order: 0,
        },
        {
          label: 'management.menu.admin.configuration',
          path: '/beheer/admin/configuratie',
          routerLink: ['/beheer/admin/configuratie'],
          order: 1,
        },
        {
          label: 'management.menu.admin.feature-flags',
          path: '/beheer/admin/feature-flags',
          routerLink: ['/beheer/admin/feature-flags'],
          order: 2,
        },
        {
          label: 'management.menu.admin.user-cleanup',
          path: '/beheer/admin/gebruikers-opschonen',
          routerLink: ['/beheer/admin/gebruikers-opschonen'],
          order: 3,
        },
      ];

      if (this.vouchersVisible) {
        adminItems.push({
          label: 'management.menu.admin.vouchers',
          path: '/beheer/vouchers',
          routerLink: ['/beheer/vouchers'],
          order: 4,
        });
      }

      groups.push({
        label: 'management.menu.admin.title',
        disableCollapsible: true,
        items: adminItems,
      });
    }

    return groups;
  }
}
